import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import SimpleHeader from './SimpleHeader';
import Footer from './Footer';
import './PhonePeTestPage.css';

// Backend API URL
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

const PhonePeTestPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  // Function to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return '#10b981'; // green
      case 'error':
        return '#ef4444'; // red
      case 'pending':
        return '#f59e0b'; // amber
      case 'info':
        return '#3b82f6'; // blue
      default:
        return '#6b7280'; // gray
    }
  };

  // Function to get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'pending':
        return '⟳';
      case 'info':
        return 'ℹ';
      default:
        return '•';
    }
  };

  // Handle test payment
  const handleTestPayment = async () => {
    setIsLoading(true);
    setTestResult(null);
    setError(null);

    try {
      // Get auth token for authenticated request
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in first to test payment');
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/payment/test-phonepe`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setTestResult(response.data);
        toast.success('Test payment order created successfully!');
      } else {
        setError(response.data);
        toast.error(response.data.error || 'Test payment failed');
      }
    } catch (err) {
      const errorData = err.response?.data || { error: err.message };
      setError(errorData);
      toast.error(errorData.error || 'Failed to create test payment');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle redirect to payment page
  const handleRedirectToPayment = () => {
    if (testResult?.paymentUrl) {
      window.open(testResult.paymentUrl, '_blank');
    }
  };

  return (
    <div className="phonepe-test-page">
      <SimpleHeader />
      <div className="phonepe-test-container">
        <div className="phonepe-test-header">
          <h1>PhonePe Integration Test</h1>
          <p className="test-description">
            This test page creates a ₹1 test payment and shows each step of the PhonePe integration process.
            Use this to debug and understand what's happening at each stage.
          </p>
        </div>

        <div className="test-controls">
          <button
            onClick={handleTestPayment}
            disabled={isLoading}
            className="test-button"
          >
            {isLoading ? 'Testing...' : 'Start ₹1 Test Payment'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="test-section error-section">
            <h2>❌ Error</h2>
            <div className="error-content">
              <p><strong>Error:</strong> {error.error || 'Unknown error'}</p>
              {error.errorDetails && (
                <div className="error-details">
                  <strong>Details:</strong>
                  <pre>{JSON.stringify(error.errorDetails, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Steps Display */}
        {testResult?.steps && (
          <div className="test-section steps-section">
            <h2>📋 Test Steps</h2>
            <div className="steps-list">
              {testResult.steps.map((step, index) => (
                <div key={index} className="step-item">
                  <div className="step-header">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(step.status) }}
                    >
                      {getStatusIcon(step.status)}
                    </span>
                    <span className="step-title">{step.step}</span>
                    <span className="step-status">{step.status.toUpperCase()}</span>
                  </div>
                  <div className="step-timestamp">
                    {new Date(step.timestamp).toLocaleTimeString()}
                  </div>
                  {step.data && (
                    <div className="step-data">
                      <pre>{JSON.stringify(step.data, null, 2)}</pre>
                    </div>
                  )}
                  {step.error && (
                    <div className="step-error">
                      <strong>Error:</strong> {step.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {testResult?.summary && (
          <div className="test-section summary-section">
            <h2>📊 Summary</h2>
            <div className="summary-grid">
              <div className="summary-item">
                <strong>Amount:</strong> {testResult.summary.amount}
              </div>
              <div className="summary-item">
                <strong>Credits:</strong> {testResult.summary.credits}
              </div>
              <div className="summary-item">
                <strong>Mode:</strong> {testResult.summary.mode}
              </div>
              <div className="summary-item">
                <strong>Transaction ID:</strong> {testResult.summary.transactionId}
              </div>
            </div>
          </div>
        )}

        {/* Payment URL */}
        {testResult?.paymentUrl && (
          <div className="test-section payment-url-section">
            <h2>🔗 Payment URL</h2>
            <div className="payment-url-content">
              <p>Payment URL generated successfully! Click the button below to open the PhonePe payment page.</p>
              <button
                onClick={handleRedirectToPayment}
                className="payment-button"
              >
                Open PhonePe Payment Page
              </button>
              <div className="url-display">
                <strong>URL:</strong>
                <code>{testResult.paymentUrl}</code>
              </div>
            </div>
          </div>
        )}

        {/* Full Response (for debugging) */}
        {testResult && (
          <div className="test-section full-response-section">
            <h2>🔍 Full Response (Debug)</h2>
            <details>
              <summary>Click to view full API response</summary>
              <pre className="full-response">{JSON.stringify(testResult, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PhonePeTestPage;

